import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AppLayout } from 'src/components/layout/app-layout';
import { useSetPageTitle } from 'src/hooks/useSetPageTitle';
import { useIsMobile } from 'src/hooks/useMobile';
import { useGetSurveyGroupPreviewQuery, useGetSurveyGroupResultsQuery } from 'src/redux/survey/survey-groups.endpoints';
import { SurveySummaryData } from 'src/redux/survey/survey.types';
import { SidebarProvider, SidebarTrigger } from 'src/components/ui/sidebar';
import { SurveyGroupSurveyFilterBar } from './components/survey-group-survey-filter-bar';
import {
  aggregateGroupPayloadTotalUsers,
} from '../view-survey/view-survey.helpers';
import { combinedQuestionsFromGroupResultsPayload } from '../view-survey/survey-group-results-merge.helpers';
import { SurveyGroupResultsSidebar } from './survey-group-results-sidebar';
import { useSurveyGroupResultsTabs } from './survey-group-results-nav';
import TrendsHeader from 'src/pages/results/components/trends/trends-header';
import { useSurveyGroupTrendsData } from './use-survey-group-trends-data';
import { buildSurveyGroupTrendsQuestionCharts } from './survey-group-trends-merge.helpers';
import SurveyGroupTrendsQuestionChart from './survey-group-trends-question-chart';
import { RefreshCw } from 'lucide-react';
import SurveyGroupCompareLoadingBar from './components/survey-group-compare-loading-bar';
import { useSurveyGroupCompareLoadingProgress } from './use-survey-group-compare-loading-progress';

const SurveyGroupTrendsPage = (): ReactElement => {
  useSetPageTitle('Survey group trends');
  const { clientId, surveyGroupId } = useParams<{ clientId?: string; surveyGroupId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobile } = useIsMobile();
  const { tabs, activeTab } = useSurveyGroupResultsTabs(surveyGroupId, clientId);

  const [selectedInterval, setSelectedInterval] = useState(7);

  const { data: groupResults, currentData, isFetching, isError } = useGetSurveyGroupResultsQuery(
    {
      groupId: surveyGroupId ?? '',
      include: 'stats,summary,trends,trends_events',
    },
    { skip: !surveyGroupId }
  );

  const resolvedGroupResults = currentData ?? (!isFetching ? groupResults : undefined);

  const { data: groupPreview } = useGetSurveyGroupPreviewQuery(surveyGroupId ?? '', {
    skip: !surveyGroupId,
  });

  const [selectedGroupSurveyIds, setSelectedGroupSurveyIds] = useState<string[]>(() => {
    const p = searchParams.get('groupSurveys');
    return p ? p.split(',').filter(Boolean) : [];
  });

  useEffect(() => {
    if (!resolvedGroupResults?.surveys?.length) {
      return;
    }
    setSelectedGroupSurveyIds((prev) => {
      if (prev.length > 0) {
        return prev;
      }
      return resolvedGroupResults.surveys.map((s) => s.ext_survey_id);
    });
  }, [resolvedGroupResults]);

  const updateGroupSurveySelection = useCallback(
    (ids: string[]) => {
      setSelectedGroupSurveyIds(ids);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (ids.length) {
            next.set('groupSurveys', ids.join(','));
          } else {
            next.delete('groupSurveys');
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const visibleGroupSurveys = useMemo(() => {
    if (!resolvedGroupResults?.surveys?.length) {
      return [];
    }
    const selected = new Set(selectedGroupSurveyIds);
    return resolvedGroupResults.surveys.filter((s) => selected.has(s.ext_survey_id));
  }, [resolvedGroupResults, selectedGroupSurveyIds]);

  const visibleExtIds = useMemo(
    () => visibleGroupSurveys.map((s) => s.ext_survey_id),
    [visibleGroupSurveys]
  );

  const surveyOrderMeta = useMemo(
    () =>
      visibleGroupSurveys.map((s) => ({
        extSurveyId: s.ext_survey_id,
        title: s.title ?? s.ext_survey_id,
      })),
    [visibleGroupSurveys]
  );

  const { trendsBySurvey, isLoading: trendsLoading, hasError: trendsPartialError } = useSurveyGroupTrendsData(
    visibleExtIds,
    selectedInterval
  );

  const showSummaries = useMemo(() => {
    return visibleGroupSurveys.some((p) => {
      const s = p.summary as SurveySummaryData | undefined;
      const q = s?.questions;
      if (!q) {
        return false;
      }
      return Array.isArray(q) ? q.length > 0 : Object.keys(q as object).length > 0;
    });
  }, [visibleGroupSurveys]);

  const mergedQuestions = useMemo(() => {
    if (!resolvedGroupResults) {
      return [];
    }
    return combinedQuestionsFromGroupResultsPayload(resolvedGroupResults, showSummaries, groupPreview);
  }, [resolvedGroupResults, showSummaries, groupPreview]);

  const aggregatedTotalUsers = useMemo(
    () => aggregateGroupPayloadTotalUsers(visibleGroupSurveys),
    [visibleGroupSurveys]
  );

  const trendCharts = useMemo(
    () =>
      buildSurveyGroupTrendsQuestionCharts(
        groupPreview?.questions ?? [],
        trendsBySurvey,
        surveyOrderMeta
      ),
    [groupPreview?.questions, trendsBySurvey, surveyOrderMeta]
  );

  const loadingOverlay = trendsLoading && visibleExtIds.length > 0;

  const isGroupResultsLoading = isFetching && resolvedGroupResults === undefined;

  const { visible: groupResultsLoadBarVisible, percent: groupResultsLoadPercent } =
    useSurveyGroupCompareLoadingProgress(isGroupResultsLoading);

  return (
    <AppLayout
      activePage="/results"
      navbarProps={{
        showBackButton: false,
        backPath: clientId ? `/client/${clientId}/reports` : '/surveys',
        tabs,
        activeTab,
        onTabChange: () => undefined,
      }}
    >
      <div className="flex h-[calc(100vh-8rem)] min-h-0 w-full flex-col">
        <SidebarProvider className="min-h-0 flex-1 flex-row">
          <div className="min-w-0 flex-1 p-4 md:p-1">
            {isMobile && (
              <div className="flex size-12 items-center justify-center">
                <SidebarTrigger />
              </div>
            )}
            {!surveyGroupId ? (
              <p className="text-sm text-gray-600">
                Missing survey group.
              </p>
            ) : null}
            {groupResultsLoadBarVisible || isGroupResultsLoading ? (
              <SurveyGroupCompareLoadingBar
                percent={groupResultsLoadPercent}
                visible={groupResultsLoadBarVisible || isGroupResultsLoading}
                title="Loading survey group results…"
                helperText="Fetching aggregated survey group data from the server. Progress is estimated until the response arrives."
              />
            ) : null}
            {isError ? (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-8 text-sm text-red-800">
                Could not load survey group results. Check that you have access to this group and try again.
              </div>
            ) : null}
            {resolvedGroupResults?.surveys?.length ? (
              <>
                <SurveyGroupSurveyFilterBar
                  surveys={resolvedGroupResults.surveys}
                  selectedExtSurveyIds={selectedGroupSurveyIds}
                  onChange={updateGroupSurveySelection}
                />
                {visibleGroupSurveys.length === 0 ? (
                  <p className="text-sm text-gray-600">
                    Select at least one survey above to load trends.
                  </p>
                ) : !groupPreview?.questions?.length ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-sm text-amber-900 mt-4">
                    Trends for this view need the survey group question map from the server. Try again after preview loads.
                  </div>
                ) : (
                  <div className="relative mt-4 max-w-5xl">
                    {loadingOverlay && (
                      <div className="absolute -inset-2 bg-gray-200/70 backdrop-blur-sm z-40 flex items-center justify-center rounded-lg pointer-events-auto min-h-[200px]">
                        <div className="flex flex-col items-center gap-3 p-4">
                          <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
                          <p className="text-sm font-medium text-gray-800">
                            Loading trends…
                          </p>
                        </div>
                      </div>
                    )}
                    <div className="flex flex-col gap-2 mb-4">
                      <p className="text-sm text-gray-600">
                        Each chart shows every selected survey’s answer lines together over time (clustered options from the group definition). Use the interval to change the bucket size.
                      </p>
                      {trendsPartialError ? (
                        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                          Some surveys failed to load trends; charts may be incomplete.
                        </p>
                      ) : null}
                      <TrendsHeader onIntervalChange={setSelectedInterval} />
                    </div>
                    <div className="flex flex-col gap-10">
                      {trendCharts.map((block, idx) => (
                        <SurveyGroupTrendsQuestionChart
                          key={block.questionClusterId}
                          block={block}
                          index={idx}
                          isLoading={trendsLoading}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </div>
          {surveyGroupId && resolvedGroupResults ? (
            <SurveyGroupResultsSidebar
              surveyGroupId={surveyGroupId}
              surveyGroupTitle={groupPreview?.name?.trim() || 'Survey group'}
              groupResults={resolvedGroupResults}
              mergedQuestions={mergedQuestions}
              aggregatedTotalUsers={aggregatedTotalUsers}
              showSummaries={showSummaries}
            />
          ) : null}
        </SidebarProvider>
      </div>
    </AppLayout>
  );
};

export default SurveyGroupTrendsPage;
